import { useState } from "react";
import { useLang, shared, CONTACTS } from "../i18n";
import { useSEO } from "../seo";
import { FadeIn } from "../components/Reveal";
import { PageHero, SectionLabel } from "../components/Bits";
import { Phone, MessageCircle, MapPin, Check } from "lucide-react";

const copy = {
  it: {
    seoTitle: "Contatti | Nicole Srl",
    seoDesc: "Parliamo del tuo immobile: proposte, vendite, situazioni complesse o consulenza. Nicole Srl, Viale Monza 355, 20126 Milano (MI).",
    kicker: "Contatti",
    lines: ["Parliamo del", "tuo immobile."],
    sub: "Che si tratti di un immobile da vendere, di un'opportunità immobiliare o di una situazione complessa, raccontaci brevemente il caso.",
    formLabel: "Il modulo",
    fields: {
      name: "Nome e cognome",
      phone: "Telefono",
      email: "Email",
      city: "Comune dell'immobile",
      type: "Tipo di richiesta",
      message: "Messaggio",
      privacy: "Ho letto e accetto la Privacy Policy e acconsento al trattamento dei dati per essere ricontattato.",
    },
    types: [
      "Voglio proporre un immobile",
      "Voglio vendere un immobile",
      "Immobile con debiti / procedura",
      "Consulenza immobiliare",
      "Frazionamento / cambio d'uso",
      "Ristrutturazione",
      "General Contractor",
      "Operazione immobiliare",
      "Altro",
    ],
    submit: "Invia richiesta",
    sending: "Invio in corso…",
    successTitle: "Richiesta ricevuta.",
    successText: "Ti ricontatteremo per approfondire l'operazione.",
    errorText: "Qualcosa non ha funzionato. Riprova o contattaci direttamente per telefono o WhatsApp.",
    invalidText: "Controlla i campi evidenziati e riprova.",
    fieldErrors: {
      name: "Inserisci nome e cognome.",
      phone: "Inserisci un numero di telefono valido.",
      email: "L'indirizzo email non sembra valido (es. nome@dominio.it).",
      city: "Inserisci il comune dell'immobile.",
      type: "Seleziona il tipo di richiesta.",
      message: "Scrivi qualche riga in più sulla richiesta.",
    },
    requiredNote: "Tutti i campi sono obbligatori.",
    contactsLabel: "Contatti diretti",
    call: "Chiama",
    whatsapp: "WhatsApp",
    emailLabel: "Scrivici",
    addressLabel: "Sede",
  },
  en: {
    seoTitle: "Contact | Nicole Srl",
    seoDesc: "Let's talk about your property: proposals, sales, complex situations or advisory. Nicole Srl, Viale Monza 355, 20126 Milano (MI).",
    kicker: "Contact",
    lines: ["Let's talk about", "your property."],
    sub: "Whether it is a property to sell, a real estate opportunity or a complex situation, briefly tell us about the case.",
    formLabel: "The form",
    fields: {
      name: "Full name",
      phone: "Phone",
      email: "Email",
      city: "Property municipality",
      type: "Request type",
      message: "Message",
      privacy: "I have read and accept the Privacy Policy and consent to the processing of my data to be contacted back.",
    },
    types: [
      "Voglio proporre un immobile",
      "Voglio vendere un immobile",
      "Immobile con debiti / procedura",
      "Consulenza immobiliare",
      "Frazionamento / cambio d'uso",
      "Ristrutturazione",
      "General Contractor",
      "Operazione immobiliare",
      "Altro",
    ],
    submit: "Send request",
    sending: "Sending…",
    successTitle: "Request received.",
    successText: "We will contact you to discuss the operation in depth.",
    errorText: "Something went wrong. Please try again or contact us directly by phone or WhatsApp.",
    invalidText: "Please check the highlighted fields and try again.",
    fieldErrors: {
      name: "Please enter your full name.",
      phone: "Please enter a valid phone number.",
      email: "The email address does not look valid (e.g. name@domain.com).",
      city: "Please enter the property municipality.",
      type: "Please select the request type.",
      message: "Please write a few more lines about your request.",
    },
    requiredNote: "All fields are required.",
    contactsLabel: "Direct contacts",
    call: "Call",
    whatsapp: "WhatsApp",
    emailLabel: "Write to us",
    addressLabel: "Office",
  },
};

const typeLabelsEn = {
  "Voglio proporre un immobile": "I want to propose a property",
  "Voglio vendere un immobile": "I want to sell a property",
  "Immobile con debiti / procedura": "Property with debts / procedure",
  "Consulenza immobiliare": "Real estate advisory",
  "Frazionamento / cambio d'uso": "Subdivision / change of use",
  "Ristrutturazione": "Renovation",
  "General Contractor": "General Contractor",
  "Operazione immobiliare": "Real estate operation",
  "Altro": "Other",
};

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-stone">{label}</span>
    {children}
    {error && <span data-testid="field-error" className="mt-1.5 block text-xs text-red-800">{error}</span>}
  </label>
);

const inputCls =
  "mt-2 w-full border-0 border-b border-ink/25 bg-transparent px-0 py-3 text-base text-ink placeholder:text-stone/60 focus:border-bronze focus:outline-none focus:ring-0 transition-colors duration-300";

export default function Contatti() {
  const { lang } = useLang();
  const t = copy[lang];
  useSEO({ title: t.seoTitle, description: t.seoDesc, path: "/contatti", lang });

  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", type: "", message: "", privacy: false, website: "" });
  const [status, setStatus] = useState("idle");
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
    setFieldErrors((fe) => (fe[k] ? { ...fe, [k]: null } : fe));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setFieldErrors({});
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          city: form.city,
          request_type: form.type,
          message: form.message,
          privacy: form.privacy,
          website: form.website,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (res.status === 422 && Array.isArray(data?.detail)) {
          const mapped = {};
          data.detail.forEach((err) => {
            const key = err.loc?.[1] === "request_type" ? "type" : err.loc?.[1];
            if (key && key !== "privacy" && key !== "website") mapped[key] = true;
          });
          setFieldErrors(mapped);
          setStatus("invalid");
          return;
        }
        throw new Error();
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div data-testid="contatti-page">
      <PageHero kicker={t.kicker} lines={t.lines} sub={t.sub} />

      <section className="mx-auto max-w-7xl px-6 pb-28 md:px-10 md:pb-40">
        <div className="grid gap-20 md:grid-cols-12">
          <div className="md:col-span-7">
            <FadeIn>
              <SectionLabel>{t.formLabel}</SectionLabel>
            </FadeIn>
            {status === "success" ? (
              <FadeIn className="mt-12">
                <div data-testid="form-success" className="border border-bronze/40 bg-dove/40 p-10 md:p-14">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bronze/15">
                    <Check size={20} strokeWidth={1.5} className="text-bronze" />
                  </span>
                  <h2 className="mt-7 font-serif text-3xl font-light tracking-tight md:text-4xl">{t.successTitle}</h2>
                  <p className="mt-4 max-w-md text-base leading-relaxed text-stone">{t.successText}</p>
                </div>
              </FadeIn>
            ) : (
              <FadeIn delay={0.1}>
                <form onSubmit={onSubmit} data-testid="contact-form" className="mt-12 space-y-9">
                  <div className="grid gap-9 sm:grid-cols-2">
                    <Field label={t.fields.name} error={fieldErrors.name && t.fieldErrors.name}>
                      <input required data-testid="field-name" value={form.name} onChange={set("name")} className={inputCls} autoComplete="name" />
                    </Field>
                    <Field label={t.fields.phone} error={fieldErrors.phone && t.fieldErrors.phone}>
                      <input required type="tel" data-testid="field-phone" value={form.phone} onChange={set("phone")} className={inputCls} autoComplete="tel" />
                    </Field>
                  </div>
                  <div className="grid gap-9 sm:grid-cols-2">
                    <Field label={t.fields.email} error={fieldErrors.email && t.fieldErrors.email}>
                      <input required type="email" data-testid="field-email" value={form.email} onChange={set("email")} className={inputCls} autoComplete="email" />
                    </Field>
                    <Field label={t.fields.city} error={fieldErrors.city && t.fieldErrors.city}>
                      <input required data-testid="field-city" value={form.city} onChange={set("city")} className={inputCls} />
                    </Field>
                  </div>
                  <Field label={t.fields.type} error={fieldErrors.type && t.fieldErrors.type}>
                    <select required data-testid="field-type" value={form.type} onChange={set("type")} className={`${inputCls} cursor-pointer appearance-none`}>
                      <option value="" disabled>—</option>
                      {t.types.map((ty) => (
                        <option key={ty} value={ty}>
                          {lang === "en" ? typeLabelsEn[ty] : ty}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t.fields.message} error={fieldErrors.message && t.fieldErrors.message}>
                    <textarea required rows={4} data-testid="field-message" value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} />
                  </Field>
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
                    <input tabIndex={-1} autoComplete="off" name="website" value={form.website} onChange={set("website")} style={{ width: 0, height: 0, opacity: 0, padding: 0, border: "none" }} />
                  </div>
                  <label className="flex cursor-pointer items-start gap-4">
                    <input
                      type="checkbox"
                      required
                      data-testid="field-privacy"
                      checked={form.privacy}
                      onChange={set("privacy")}
                      className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer appearance-none border border-ink/40 transition-colors duration-300 checked:border-bronze checked:bg-bronze"
                    />
                    <span className="text-xs leading-relaxed text-stone">{t.fields.privacy}</span>
                  </label>
                  {status === "invalid" && (
                    <p data-testid="form-invalid" className="text-sm text-red-800">{t.invalidText}</p>
                  )}
                  {status === "error" && (
                    <p data-testid="form-error" className="text-sm text-red-800">{t.errorText}</p>
                  )}
                  <button
                    type="submit"
                    data-testid="form-submit"
                    disabled={status === "sending"}
                    className="group inline-flex items-center gap-3 bg-ink px-9 py-4 text-[11px] font-medium uppercase tracking-[0.2em] text-ivory transition-colors duration-500 hover:bg-bronze disabled:opacity-60"
                  >
                    {status === "sending" ? t.sending : t.submit}
                  </button>
                </form>
              </FadeIn>
            )}
          </div>

          <div className="md:col-span-4 md:col-start-9">
            <FadeIn delay={0.15}>
              <SectionLabel>{t.contactsLabel}</SectionLabel>
              <div className="mt-10 space-y-10">
                <div>
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-stone">
                    <MapPin size={13} strokeWidth={1.5} /> {t.addressLabel}
                  </div>
                  <p className="mt-3 font-serif text-xl font-light leading-snug">
                    Nicole Srl
                    <br />
                    {CONTACTS.address}
                  </p>
                  <p data-testid="contatti-upcoming" className="mt-3 text-[11px] uppercase tracking-[0.18em] text-stone/80">
                    {shared[lang].upcoming}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-stone">
                    <Phone size={13} strokeWidth={1.5} /> {CONTACTS.phone}
                  </div>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row md:flex-col">
                    <a
                      href={CONTACTS.phoneHref}
                      data-testid="call-button"
                      className="inline-flex items-center justify-center gap-3 bg-ink px-7 py-3.5 text-[11px] font-medium uppercase tracking-[0.2em] text-ivory transition-colors duration-500 hover:bg-bronze"
                    >
                      <Phone size={14} strokeWidth={1.5} /> {t.call}
                    </a>
                    <a
                      href={CONTACTS.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="whatsapp-button"
                      className="inline-flex items-center justify-center gap-3 border border-ink/60 px-7 py-3.5 text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-500 hover:bg-ink hover:text-ivory"
                    >
                      <MessageCircle size={14} strokeWidth={1.5} /> {t.whatsapp}
                    </a>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
